// src/contracts/templates/HICTemplate.js
// Canonical defaults for a new Home Improvement Contract under the new section
// model: every body section is either a `header` (auto-numbered, contains a
// single rich-text HTML body) or a `table` (heading + columns/rows, no number).
//
// Auto-numbering starts at 2 because section 1 ("List of Documents to be
// Incorporated") is pinned and rendered separately.

const uid = (prefix = 'id') => `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;

function defaultExhibits() {
  return [
    { exhibitId: 'ex_gli',  kind: 'gli',          title: 'Commercial General Liability Insurance', regulated: true, data: {} },
    { exhibitId: 'ex_wc',   kind: 'wc',           title: "Workers' Compensation Insurance",        regulated: true, data: {} },
    { exhibitId: 'ex_3day', kind: 'cancel_3day',  title: '3 day right to cancel',                  regulated: true, data: {} },
    { exhibitId: 'ex_7day', kind: 'cancel_7day',  title: '7 day right to cancel',                  regulated: true, data: {} },
    { exhibitId: 'ex_co',   kind: 'change_order', title: 'Sample change order form',               data: {} },
    {
      exhibitId: 'ex_mat',
      kind: 'material_list',
      title: 'Material List',
      data: {
        table: {
          columns: [
            { header: 'Item',  width: 4 },
            { header: 'Total', width: 1 },
            { header: 'Make',  width: 2 },
            { header: 'Model', width: 3 },
          ],
          rows: [['', '', '', '']],
        },
      },
    },
  ];
}

// Convenience builders for the default sections.
const header = (heading, html, opts = {}) =>
  ({ id: uid('sec'), type: 'header', heading, html, ...opts });
const table = (heading, columns, rows, opts = {}) =>
  ({ id: uid('sec'), type: 'table', heading, columns, rows, ...opts });

// Inline templating helpers for the rich-text bodies. Bullets become real
// <ul><li>; exhibit references use <span data-ref="..."> spans that get
// resolved live at render time.
const p  = (html) => `<p>${html}</p>`;
const ul = (items) => `<ul>${items.map(i => `<li>${i}</li>`).join('')}</ul>`;
const ref = (id) => `<span data-ref="${id}">EXHIBIT ?</span>`;

function defaultSections() {
  return [
    header('Scope of work', [
      p('Contractor shall provide all labor and materials, and perform all work necessary for the completion of the residence, structure, or improvements as described in this contract signed by both Owner and Contractor ("Project").'),
      p('The scope of work for the proposed project defines the tasks, deliverables, and materials required to replace the existing electrical system with a fully functional and code-compliant installation. The work includes removal of all existing wiring and components, followed by installation of new wiring and equipment in accordance with the National Electrical Code (NEC) and the Authority Having Jurisdiction (AHJ).'),
      p('Any modifications will be documented and mutually agreed upon in writing before execution of the contractual agreement to uphold project quality and objectives.'),
      p('The following tasks outline the electrical system replacement and upgrades to be performed:'),
      ul([
        'Bedrooms: Install recessed lighting in all bedrooms, with a fan/light combo in the master bedroom. Equip all bedroom lighting with NEC-compliant dimmable wall switches.',
        'Restrooms: Install one recessed light and one vanity light fixture in each restroom, with an additional recessed light and provisional power for a bidet in the master restroom. Fit each restroom with one NEC-compliant GFCI receptacle.',
        'Receptacles: Install NEC-compliant tamper-resistant or GFCI receptacles in bedrooms, restrooms, hallways, family room, office, vestibule, dining room, laundry room, garage, and exterior walls as required.',
        'Kitchen: Provide two dedicated small-appliance circuits for counter spaces, one dedicated refrigerator circuit, dedicated power for the electric range, dishwasher, garbage disposal, island, hood, and under-cabinet lighting.',
        'Dining Room and Laundry Room: Install recessed lighting in both areas.',
        'Living Room: Provide power with a switch for an electric fireplace and install swivel-type recessed lights.',
        'Office: Install two recessed lights.',
        'Garage: Install a 200A subpanel and two 4-foot shop lights.',
        'Exterior: Install three wall sconce lights and four motion-activated security lights.',
        `General: Final quantities and makes, see ${ref('ex_mat')} attached below.`,
      ]),
    ].join('')),

    table('Wiring summary',
      [
        { header: 'Description', width: 3 },
        { header: 'Breaker size', width: 1 },
        { header: 'Wire size', width: 1 },
        { header: '# / type of switches', width: 2 },
        { header: '# of lights', width: 1 },
        { header: 'Amount', width: 1 },
      ],
      [
        ['Receptacles', '20A', '12 AWG', '', '', ''],
        ['Light switches', '15A', '14 AWG', '', '', ''],
        ['Low voltage wire', '0', '20 AWG', '', '', ''],
        ['Door bell / chimes', '20A', '12 AWG', '', '', ''],
        ['Smoke detectors', 'N/A', 'N/A', '', '', ''],
        ['Mini split', '40A', '8 AWG', '', '', ''],
        ['Electric dryer', '30A', '8 AWG', '', '', ''],
        ['Hood / fan', '20A', '12 AWG', '', '', ''],
        ['Dishwasher', '20A', '12 AWG', '', '', ''],
        ['Range', '50A', '6 AWG', '', '', ''],
        ['EV charger', '50A', '6 AWG', '', '', ''],
        ['Refrigerator', '20A', '12 AWG', '', '', ''],
      ],
    ),

    table('Room count',
      [
        { header: 'Room', width: 2 },
        { header: '15A plugs', width: 1 },
        { header: '15A switch', width: 1 },
        { header: '3W switch', width: 1 },
        { header: '4W switch', width: 1 },
        { header: 'Lights', width: 1 },
        { header: '15A GFI', width: 1 },
        { header: 'Smoke', width: 1 },
        { header: 'Fan', width: 1 },
        { header: 'Amount', width: 1 },
      ],
      [
        ['Kitchen', '', '', '', '', '', '', '', '', ''],
        ['Dining room', '', '', '', '', '', '', '', '', ''],
        ['Living room', '', '', '', '', '', '', '', '', ''],
        ['Office', '', '', '', '', '', '', '', '', ''],
        ['Stairway / closet', '', '', '', '', '', '', '', '', ''],
        ['Laundry', '', '', '', '', '', '', '', '', ''],
        ['½ bath', '', '', '', '', '', '', '', '', ''],
        ['Hallway', '', '', '', '', '', '', '', '', ''],
        ['Master bedroom', '', '', '', '', '', '', '', '', ''],
        ['Bedroom #1', '', '', '', '', '', '', '', '', ''],
        ['Bedroom #2', '', '', '', '', '', '', '', '', ''],
        ['Bedroom #3', '', '', '', '', '', '', '', '', ''],
        ['Garage', '', '', '', '', '', '', '', '', ''],
        ['Master bathroom', '', '', '', '', '', '', '', '', ''],
        ['Bathroom #1', '', '', '', '', '', '', '', '', ''],
      ],
    ),

    header('Work site',
      p('The Project shall be constructed on the property of Owner at the address listed above (hereafter "the Work Site"). Owner hereby authorizes Contractor to commence and complete the usual and customary excavation and grading on the Work Site as may be required in the judgment of the Contractor to complete the Project. Unless called for in the drawings or specifications, no landscaping, finish grading, filling or excavation is to be performed at the Work Site by the Contractor.')
    ),

    header('Commencement and completion of work to be performed', [
      p('<b>(a)</b> Contractor shall substantially commence work as described in the scope of work.'),
      p('<b>(b)</b> Approximate start date: ____________'),
      p('<b>(c)</b> Approximate date of substantial completion: ____________'),
      p('Contractor shall not be liable, or in breach of this Contract, for any delay due to circumstances beyond its control including strikes, casualty, acts of God, or general unavailability of materials.'),
    ].join('')),

    header('Permits',
      p('Homeowner shall apply for and obtain such permits and regulatory approvals as may be required by the local municipal/county government, the cost thereof shall not be included as part of the Project price.')
    ),

    header('Insurance',
      p(`For general liability and workers compensation see ${ref('ex_gli')} &amp; ${ref('ex_wc')}.`)
    ),

    header('Title',
      p('The homeowner or their authorized representative shall provide the contractor with documentation verifying authority to contract for electrical work at the work site, including proof of title or trust authorization for the property.')
    ),

    header('Extra work and change orders',
      p(`Extra work and change orders become part of this Contract once the order is prepared in writing and signed by both parties prior to the commencement of any work covered by the new change order. The order must describe the scope of the extra work or change, the cost to be added or subtracted from the Contract, and the effect the order will have on the schedule of progress payments (if applicable) or the completion date. A sample change order form is attached as ${ref('ex_co')}.`)
    ),

    header('Pricing', [
      p('<b>(a) Contract price (fixed fee):</b> Owner agrees to pay Contractor the sum set forth in the contract details above for performing the services set forth in the scope of work. Contractor shall be paid according to the payment schedule below.'),
      p('<b>(b) Down payment:</b> as set forth in the schedule below. The down payment may not exceed $1,000 or 10 percent of the contract price, whichever is less.'),
      p('<b>(c) Schedule of progress payments:</b>'),
      p('The schedule of progress payments must specifically describe each phase of work, including the type and amount of work or services scheduled to be supplied in each phase, along with the amount of each proposed progress payment. IT IS AGAINST THE LAW FOR A CONTRACTOR TO COLLECT PAYMENT FOR WORK NOT YET COMPLETED, OR FOR MATERIALS NOT YET DELIVERED. HOWEVER, A CONTRACTOR MAY REQUIRE A DOWNPAYMENT.'),
      p('Upon satisfactory payment being made for any portion of the work performed, the contractor, prior to any further payment being made, shall furnish to the person contracting for the home improvement work a full and unconditional release from any potential lien claimant claim or mechanics lien authorized pursuant to Sections 8400 and 8404 of the Civil Code for that portion of the work for which payment has been made.'),
    ].join(''), { regulated: true }),

    table('Payment schedule',
      [
        { header: 'Amount', width: 1 },
        { header: 'Work or service to be performed', width: 3 },
        { header: 'Materials or equipment to be supplied', width: 3 },
      ],
      [
        ['$0.00', 'Mobilization (first day on the job)', 'Rough-in wiring &amp; electrical boxes to be supplied'],
        ['$0.00', 'Completion of rough electrical', 'Sub-panel'],
        ['$0.00', 'Substantially complete work', 'Switches, receptacles, fixtures'],
        ['$0.00', 'Remaining punch-list items', ''],
      ],
    ),

    header('Late payment / default', [
      p('A failure to make payment for a period more than three (3) days from the due date shall be deemed a material breach of this Contract. If payment is not made when due, Contractor may suspend work on the job until such time as all payments due have been made without breach of the Contract pending payment or resolution of any dispute. Owner agrees to pay a late charge of 1% of all payments that are more than three (3) days late plus at the rate of 1% per month.'),
      p('If payment remains unpaid for 30 days after the due date, the account is in default, and the contractor may terminate the Contract and pursue all legal remedies, including full payment, accrued fees, interest, and reasonable legal / collection costs.'),
    ].join('')),

    header('Destruction and damage',
      p('If the Project is destroyed or damaged for any reason, except where such destruction or damage was caused by the sole negligence of the Contractor or its subcontractors, Owner shall pay Contractor for any additional work done by Contractor in rebuilding or restoring the Project to its condition prior to such destruction or damage. If the estimated cost of replacing work already accomplished by Contractor exceeds 20 percent of the Contract price, either the Contractor or Owner may terminate this Contract.')
    ),

    header('Assignment',
      p("Neither party may assign this Contract, or payments due under the Contract, without the other party's written consent. Any such assignment shall be void and of no effect.")
    ),

    header('Interpretation', [
      p('<b>(a) Interpretation of Documents.</b> The Contract, layout sketch, and material list are intended to supplement one another. In the event of a conflict, the material list shall control the layout sketch, and the Contract shall control both.'),
      p('<b>(b) Entire Agreement.</b> This Contract constitutes the entire agreement of the parties. No other agreements, oral or written, pertaining to the work to be performed under this Contract exists between the parties. This Contract may only be modified only by a written agreement signed by both parties.'),
      p('<b>(c) Governing Law.</b> This Contract shall be interpreted and governed in accordance with the laws of the State of California.'),
    ].join('')),

    header("Attorneys' fees and costs",
      p('If any party to this Contract brings a cause of action against the other party arising from or relating to this Contract, the prevailing party in such proceeding shall be entitled to recover reasonable attorney fees and court costs.')
    ),

    header('Performance', [
      p('<b>(a)</b> Contractor may, at its discretion, engage licensed subcontractors to perform work pursuant to this Contract provided Contractor shall remain fully responsible for the proper completion of the Project.'),
      p('<b>(b)</b> All work shall be completed in a workman-like manner and in compliance with all building codes and applicable laws.'),
      p('<b>(c)</b> Contractor agrees to remove all debris generated by the electrical work and leave the work areas in broom clean condition.'),
    ].join('')),

    header('Warranty', [
      p("Contractor agrees to repair or replace any defective or malfunctioning switches, outlets, breakers, trim fittings, and equipment or fixtures (supplied as part of this contract and listed herein) for a period of 1yr, unless the manufacturer's warranty is less than 1yr. Contractor agrees to repair or replace any wiring that fails to carry its designed load for a period of 1yr. Contractor shall not be responsible after the above-stated period. Contractor shall not be responsible for Owners' / Builders' negligence."),
      p('Contractor hereby assigns to Owner all warranties on materials as provided by the manufacturer of such materials.'),
    ].join('')),

    header('Required notices', [
      p('<b>(a)</b> You are entitled to a completely filled in copy of this Contract, signed by both you and the Contractor, before any work may be started.'),
      p(`<b>(b)</b> A notice concerning commercial general liability insurance is attached to this Contract as ${ref('ex_gli')}.`),
      p(`<b>(c)</b> A notice concerning workers' compensation insurance is attached to this Contract as ${ref('ex_wc')}.`),
      p(`<b>(d) Change Orders:</b> see ${ref('ex_co')} for a sample change order form. The Owner may not require a Contractor to perform extra or change-order work without providing written authorization prior to the commencement of any work covered by the new change order.`),
      p("<b>(e) MECHANICS LIEN WARNING:</b> Anyone who helps improve your property, but who is not paid, may record what is called a mechanics' lien on your property. A mechanics' lien is a claim, like a mortgage or home equity loan, made against your property and recorded with the county recorder. Even if you pay your contractor in full, unpaid subcontractors, suppliers, and laborers who helped to improve your property may record mechanics' liens and sue you in court to foreclose the lien. If a court finds the lien is valid, you could be forced to pay twice or have a court officer sell your home to pay the lien."),
      p("<b>(f) Information about the Contractors' State License Board (CSLB):</b> CSLB is the state consumer protection agency that licenses and regulates construction contractors. Contact CSLB for information about the licensed contractor you are considering. Use only licensed contractors."),
    ].join(''), { regulated: true }),

    header('Right to cancel', [
      p('Under certain circumstances the law gives you a right to cancel a contract. Check the appropriate box below describing your right to cancel this Contract:'),
      p("<b>(i)</b> This Contract was negotiated at the Contractor's place of business. Consequently, the law does not provide a right to cancel."),
      p(`<b>(ii)</b> This Contract was not negotiated at the Contractor's place of business. Consequently, the law requires that the Contractor give you a notice explaining your right to cancel. See ${ref('ex_3day')}.`),
      p(`<b>(iii)</b> This Contract is for the repair or restoration of residential premises damaged by any sudden or catastrophic event for which a state of emergency has been declared. See ${ref('ex_7day')}.`),
    ].join(''), { regulated: true }),
  ];
}

export default function HICTemplate() {
  return {
    type: 'HIC',
    formData: { sections: defaultSections() },
    exhibits: defaultExhibits(),
  };
}

export { uid, defaultSections, defaultExhibits };
